import { Component, computed, inject, Input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  currentLabelPaymentStatus,
  PaymentStatusColor,
  PaymentTypeIcon
} from '@views/common/models/payment-types.enum';
import { Station } from '@vueling-ng/vy-cdk-searcher';
import {
  Basket,
  BasketConcept,
  BasketFlightInfo,
  BasketHeaderTypeEnum,
  BasketHeaderVariantEnum,
  BasketSection,
  BasketStatusType,
  Flight,
  ItineraryTypeEnum
} from '@vueling-ng/vy-ds/src/lib/components/composed/basket';
import { AccordionTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/accordion';
import { BoxLevelEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/box';
import { IconTextSizeEnum, IconTextTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/icon-text';
import { ProcessMessageSizeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/process-message';
import { LanguageService } from 'app/services/internal/language/language.service';
import { FlightJourney, PaymentDetails, PaymentElement } from 'app/views/booking-detail/models/booking-details.model';
import { otherConcepts } from 'app/views/booking-detail/models/payment-details.model';
import { DsComponentsMapper } from 'app/views/booking-detail/services/ds-components.mapper';
import { PaymentDetailsViewModel } from './models/payment-config';

@Component({
  selector: 'app-payment-detail',
  templateUrl: './payment-detail.component.html',
  styleUrl: './payment-detail.component.scss'
})
export class PaymentDetailComponent {
  @Input({ required: true })
  public paymentDetails: PaymentDetails | undefined = undefined;
  @Input({ required: true })
  public currencyCode: string | undefined = undefined;
  @Input({ required: true })
  public itineraryType: ItineraryTypeEnum = ItineraryTypeEnum.Unknown;
  @Input({ required: true })
  public flightJourneys: FlightJourney[] = [];
  @Input({ required: true })
  public stations: Station[] = [];
  @Input({ required: true })
  public recordLocator: string | undefined = undefined;

  private readonly _translatePipe = inject(TranslatePipe);
  private readonly _dsComponentsMapper = inject(DsComponentsMapper);
  private readonly _languageService = inject(LanguageService);

  public readonly currentLang = this._languageService.currentLanguage;
  public viewModel = computed(() => {
    this.currentLang();
    return this.reloadViewModel();
  });
  public readonly accordionType: AccordionTypeEnum = AccordionTypeEnum.MULTIPLE;
  public readonly iconType: IconTextTypeEnum = IconTextTypeEnum.BODY_M;
  public readonly iconSize: IconTextSizeEnum = IconTextSizeEnum.S;
  public readonly boxLevelEnum = BoxLevelEnum;
  public readonly basketHeaderType: BasketHeaderTypeEnum = BasketHeaderTypeEnum.INBOUND;
  public readonly variant: BasketHeaderVariantEnum = BasketHeaderVariantEnum.LIGHT;
  public readonly paymentTypeLogo = PaymentTypeIcon;
  public readonly processMessageType = PaymentStatusColor;
  public readonly processMessageSize = ProcessMessageSizeEnum.S;
  public readonly status = BasketStatusType.Loaded;
  public readonly currentLabelPaymentStatus = currentLabelPaymentStatus;

  private reloadViewModel(): PaymentDetailsViewModel {
    const basket = this.initializeBasket();
    const flights = this.flightJourneys.flatMap((journey) =>
      this._dsComponentsMapper.mapFlightJourneyData(journey, this.stations)
    );

    basket.flights?.forEach((flight) => {
      flight.concepts = flight.concepts?.flatMap((concept) => {
        const singularOrPlural = concept.itemCount > 1 ? '.plural' : '.singular';
        const label = concept.isIncluded
          ? `ds.basket.concepts.bundle.${concept.code}`
          : `ds.basket.concepts.${concept.code}${singularOrPlural}`;
        return {
          ...concept,
          label: this._translatePipe.transform(label, {
            amount: concept.itemCount
          }) as string
        };
      });
    });

    basket.services?.forEach((service) => {
      const count = service.itemCount > 1 ? '.plural' : '.singular';
      service.label = this._translatePipe.transform(
        `ds.basket.common.services.service.${service.code.toLowerCase()}${count}`,
        {
          count: service.itemCount
        }
      ) as string;
    });

    basket.extraSections?.[0]?.concepts?.forEach((concept) => {
      concept.label = this._translatePipe.transform(
        `${basket.extraSections![0].conceptCopyPath}.${concept.code}`
      ) as string;
    });

    basket.discounts?.forEach((discount) => {
      discount.label = this._translatePipe.transform(`ds.basket.discounts.${discount.code}`) as string;
    });

    return {
      basket,
      flights,
      paymentDetails: this.paymentDetails!
    };
  }

  private initializeBasket(): Basket {
    return {
      currencyCode: this.currencyCode!,
      itineraryType: this.itineraryType,
      flights: this.mapFlights(),
      discounts: this.mapDiscounts(),
      services: this.mapServices(),
      extraSections: this.paymentDetails?.globalConcepts ? [this.createOtherConceptsSection()] : [],
      totalAmount: this.paymentDetails!.bookingTotalAmount,
      totalAmountPerPax: this.paymentDetails!.amountDue
    };
  }

  private mapFlights(): Flight[] {
    return this.flightJourneys.flatMap((journey, index) => {
      const taxes: BasketConcept[] | undefined = this.paymentDetails?.journeys?.at(index)?.taxes?.flatMap((tax) => {
        return {
          itemAmount: tax.amount ?? 0,
          totalAmount: tax.amount ?? 0,
          code: tax.code,
          itemCount: 1,
          isIndent: true,
          isIncluded: true
        };
      });
      const totalAmountTaxes =
        taxes?.reduce((acc, item) => {
          return acc + item.itemAmount;
        }, 0) ?? 0;

      const parentTax: BasketConcept = {
        itemAmount: totalAmountTaxes,
        totalAmount: totalAmountTaxes,
        code: 'JourneyTotalFees',
        itemCount: 1
      };

      const concepts =
        this.paymentDetails?.journeys?.at(index)?.elements?.flatMap((element) => this.mapServiceObject(element)) ?? [];
      return {
        flightInfo: this.mapFlightInfo(journey),
        totalAmount: this.paymentDetails?.journeys?.at(index)?.total ?? 0,
        concepts: [...concepts, parentTax, ...(taxes ?? [])]
      };
    });
  }

  private mapFlightInfo(journey: FlightJourney): BasketFlightInfo[] {
    return journey.segments.flatMap((segment) => {
      return {
        departureStation: segment.iataOrigin,
        arrivalStation: segment.iataDestination,
        departureDate: segment.departureDate.toString(),
        arrivalDate: segment.arrivalDate.toString(),
        flightIdentifier: segment.flightNumber
      };
    });
  }

  private mapDiscounts(): BasketConcept[] {
    if (!this.paymentDetails?.discounts) {
      return [];
    }
    return [
      {
        code: this.paymentDetails.discounts.code,
        itemCount: 1,
        itemAmount: this.paymentDetails.discounts.amount ?? 0,
        totalAmount: this.paymentDetails.discounts.amount ?? 0
      }
    ];
  }

  private mapServices(): BasketConcept[] {
    if (!this.paymentDetails?.otherServices) {
      return [];
    }
    return this.paymentDetails.otherServices.flatMap((service) => this.mapServiceObject(service));
  }

  private mapServiceObject(element: PaymentElement): BasketConcept {
    return {
      ...element,
      totalAmount: element.itemAmount
    };
  }

  private createOtherConceptsSection(): BasketSection {
    const basketSection: BasketSection = {
      title: otherConcepts.title,
      totalLabel: otherConcepts.total,
      conceptCopyPath: otherConcepts.conceptPath,
      concepts: this.mapGlobalTaxes()
    };

    return basketSection;
  }

  private mapGlobalTaxes(): BasketConcept[] {
    if (!this.paymentDetails?.globalConcepts) {
      return [];
    }
    return this.paymentDetails.globalConcepts.flatMap((concept) => {
      const basketConcept: BasketConcept = {
        code: concept.code,
        totalAmount: concept.amount!,
        itemAmount: concept.amount!,
        itemCount: 0
      };
      return basketConcept;
    });
  }
}
